import { Component, ErrorHandler, OnDestroy, ChangeDetectionStrategy, signal } from '@angular/core'
import { FieldTree, form, required } from '@angular/forms/signals'

import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'

import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'

import { KirjanpidonLadattavaAineisto, KirjanpidonLadattavanAineistonTila, KirjanpidonLadattavanAineistonTyyppi } from '@lemon/core-firebase/model/kirjanpidon-aineisto'
import {
  AnnaKirjanpidonAineistonLahetystiedotPyynto,
  AnnaKirjanpidonAineistonLahetystiedotVastaus,
  KirjanpidonLadattavanAineistonLahetysAsiakkaalleTyojonodata,
  KirjanpidonLadattavanAineistonTyojonodata,
} from '../../_jaettu-lemonator/model/kirjanpito'

import { Asiakas, Kayttaja, Tilikausi } from 'app/_jaettu-lemonator/model/asiakas'
import { KirjanpidonLadattavanAineistonLinkkipyynto, KirjanpidonLadattavanAineistonLinkkivastaus } from '../../_jaettu-lemonator/model/kirjanpito'

import { TuettuKieli } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'

import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { DebugService } from 'app/_angular/service/debug.service'
import { FirebaseLemonaid, FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { AreYouSureDialog, AreYouSureDialogData } from 'app/_jaettu-angular/_components/are-you-sure.dialog'
import { Observable, Subject, combineLatest, firstValueFrom, of } from 'rxjs'
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators'
import { KirjanpitoUriService } from '../../_jaettu-lemonator/service/kirjanpito-uri.service'
import { KirjanpitoAineistonLahetysDialog, KirjanpitoAineistonLahetysDialogData, KirjanpitoAineistonLahetysDialogResult } from './kirjanpito-aineiston-lahetys.dialog'
import { KirjanpitoAineistonLatausUrlDialog, KirjanpitoAineistonLatausUrlDialogData } from './kirjanpito-aineiston-lataus-url.dialog'

interface KirjanpidonLadattavaAineistoNaytettava extends KirjanpidonLadattavaAineisto {
  kn: string
  kuittaajanTiedot: Pick<Kayttaja, 'etunimi' | 'sukunimi'>
}

interface AineistonLatausLomake {
  alkaa: Date | null
  loppuu: Date | null
  kieli: TuettuKieli
}

@Component({
  templateUrl: './kirjanpito-aineiston-lataus.component.html',
  styleUrls: ['./kirjanpito-aineiston-lataus.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KirjanpitoAineistonLatausComponent implements OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>()

  latauksetUriObservable: Observable<string>
  latauksetEncodedUriObservable: Observable<string>
  kirjanpitajaOnDevaajaObservable: Observable<boolean>

  asiakasObservable: Observable<Asiakas>
  ladattavatAineistotObservable: Observable<KirjanpidonLadattavaAineistoNaytettava[]>

  lomake: FieldTree<AineistonLatausLomake>

  readonly alkaaMaxDate = signal<Date>(null)
  readonly loppuuMinDate = signal<Date>(null)

  valittavatKielet: { nimi: string; tunniste: TuettuKieli }[] = [
    { nimi: 'Suomi', tunniste: 'fi' },
    { nimi: 'Englanti', tunniste: 'en' },
  ]

  constructor(
    private _errorHandler: ErrorHandler,
    private _dialog: MatDialog,
    private _ladataanService: LadataanService,
    private _formValidationService: FormValidationService,
    private _asiakasService: AsiakasService,
    private _dateService: DateService,
    private _firebase: FirebaseLemonator,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _timestampService: TimestampService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _debugService: DebugService,
    private _snackbar: MatSnackBar,
  ) {
    // Debug thingies
    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable
    this.latauksetUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(avainTiedot => {
        if (avainTiedot?.avain) {
          return 'customers/' + avainTiedot.avain + '/downloadable-accounting-material'
        }
        return ''
      }),
    )
    this.latauksetEncodedUriObservable = this.latauksetUriObservable.pipe(
      map(uri => {
        return this._debugService.createFirestoreLinkLemonaid(uri)
      }),
    )

    this.asiakasObservable = this._asiakasService.nykyinenAsiakasObservable

    this.lomake = form(signal<AineistonLatausLomake>({ alkaa: null, loppuu: null, kieli: 'fi' }), polku => {
      required(polku.alkaa)
      required(polku.loppuu)
      required(polku.kieli)
    })

    arvonMuutokset(this.lomake.alkaa)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(alkupvm => {
        if (alkupvm) {
          this.loppuuMinDate.set(alkupvm) // this._dateService.lisaaKuukausia(alkupvm, 1)
        } else {
          this.loppuuMinDate.set(null)
        }
      })

    arvonMuutokset(this.lomake.loppuu)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(loppupvm => {
        if (loppupvm) {
          this.alkaaMaxDate.set(loppupvm) // this._dateService.lisaaKuukausia(loppupvm, -1)
        } else {
          this.alkaaMaxDate.set(null)
        }
      })

    this._asiakasService.nykyinenAsiakasObservable
      .pipe(
        filter(asiakas => !!asiakas),
        take(1),
      )
      .subscribe(asiakas => {
        if (asiakas.tilikaudet) {
          const now = this._dateService.currentLocalDate()
          let exactHit: Tilikausi = null
          let closestPrevious: Tilikausi = null
          let closestNext: Tilikausi = null
          for (const tilikausi of asiakas.tilikaudet) {
            if (this._dateService.compareLocalDates(tilikausi.alkaa, '<=', now) && this._dateService.compareLocalDates(tilikausi.loppuu, '>=', now)) {
              exactHit = tilikausi
            } else if (this._dateService.compareLocalDates(tilikausi.loppuu, '<', now)) {
              if (closestPrevious === null || this._dateService.compareLocalDates(tilikausi.loppuu, '>', closestPrevious.loppuu)) {
                closestPrevious = tilikausi
              }
            } else if (this._dateService.compareLocalDates(tilikausi.alkaa, '>', now)) {
              if (closestNext === null || this._dateService.compareLocalDates(tilikausi.alkaa, '<', closestNext.alkaa)) {
                closestNext = tilikausi
              }
            }
          }
          if (closestPrevious) {
            asetaArvo(this.lomake.alkaa, this._dateService.localDateToDate(closestPrevious.alkaa))
            asetaArvo(this.lomake.loppuu, this._dateService.localDateToDate(closestPrevious.loppuu))
          } else if (exactHit) {
            asetaArvo(this.lomake.alkaa, this._dateService.localDateToDate(exactHit.alkaa))
            asetaArvo(this.lomake.loppuu, this._dateService.localDateToDate(exactHit.loppuu))
          } else if (closestNext) {
            asetaArvo(this.lomake.alkaa, this._dateService.localDateToDate(closestNext.alkaa))
            asetaArvo(this.lomake.loppuu, this._dateService.localDateToDate(closestNext.loppuu))
          }
        }
      })

    const raakadataObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(asiakas => {
        if (!asiakas) {
          return of<KirjanpidonLadattavaAineisto[]>(null as KirjanpidonLadattavaAineisto[])
        }
        const listausUri = 'customers/' + asiakas.avain + '/downloadable-accounting-material'
        return this._firebaseLemonaid
          .firestoreCollection<KirjanpidonLadattavaAineisto>(listausUri)
          .orderBy('c', 'desc')
          .limit(200)
          .listenSnapshots()
          .pipe(
            switchMap(snapshots =>
              Promise.all(
                snapshots.map(async snap => {
                  const data = snap.data()
                  // Lähetys- ja kuittaustiedot tallentuvat aina dokumenttiin, jonka id === l. Jos l ei ole
                  // tämän dokumentin oma id, tiedot ovat eri dokumentissa ja ne pitää hakea ja yhdistää.
                  if (snap.id === data.l) {
                    return data
                  }
                  const lahetystiedot = await this._firebaseLemonaid.firestoreDoc<KirjanpidonLadattavaAineisto>(listausUri + '/' + data.l).get()
                  if (!lahetystiedot) {
                    return data
                  }
                  return {
                    ...data,
                    lahetettyAsiakkaalle: lahetystiedot.lahetettyAsiakkaalle,
                    lahettanytAsiakkaalle: lahetystiedot.lahettanytAsiakkaalle,
                    vastaanottajat: lahetystiedot.vastaanottajat,
                    linkinAvain: lahetystiedot.linkinAvain,
                    voimassaoloPaattyy: lahetystiedot.voimassaoloPaattyy,
                    kuitattuNoudetuksi: lahetystiedot.kuitattuNoudetuksi,
                    kuittaaja: lahetystiedot.kuittaaja,
                    kuittauksenAineistonLatausSessionAvain: lahetystiedot.kuittauksenAineistonLatausSessionAvain,
                  }
                }),
              ),
            ),
          )
      }),
    )

    this.ladattavatAineistotObservable = combineLatest([
      raakadataObservable,
      this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable,
      this._asiakasService.nykyisenAsiakkaanKayttajatObservable,
    ]).pipe(
      map(([raakadata, kirjanpitajatNimitiedotMap, kayttajat]) => {
        if (!raakadata || !kirjanpitajatNimitiedotMap) {
          return []
        }

        return raakadata.map(raaka => {
          const nimitiedot = kirjanpitajatNimitiedotMap.get(raaka.u)
          const kuittaajaKayttaja = kayttajat.find(k => k.avain === raaka.kuittaaja)
          const naytettava: KirjanpidonLadattavaAineistoNaytettava = {
            ...raaka,
            kn: nimitiedot ? nimitiedot.etunimi + ' ' + nimitiedot.sukunimi : '',
            kuittaajanTiedot: kuittaajaKayttaja ? { etunimi: kuittaajaKayttaja.etunimi, sukunimi: kuittaajaKayttaja.sukunimi } : null,
          }
          return naytettava
        })
      }),
    )
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  async luoAineisto() {
    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    const kirjanpitaja = await firstValueFrom(this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable.pipe(filter(kirj => !!kirj)))

    const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasObservable.pipe(filter(asks => !!asks)))

    const alkupvm = this.lomake.alkaa().value()
    const loppupvm = this.lomake.loppuu().value()

    const tyojonoAvain = this._firebase.firestoreCreateId()
    const tyojonoEntry: KirjanpidonLadattavanAineistonTyojonodata = {
      asiakasAvain: asiakas.avain,
      ladattavanAineistonAvain: tyojonoAvain,
      lisatty: this._timestampService.now(),
    }
    const listausEntry: KirjanpidonLadattavaAineisto = {
      u: kirjanpitaja.uid,
      l: tyojonoAvain,
      a: asiakas.avain,
      s: this._dateService.dateToNumber(alkupvm),
      e: this._dateService.dateToNumber(loppupvm),
      k: this.lomake.kieli().value(),
      b: KirjanpidonLadattavanAineistonTila.PROSESSOIDAAN,
      c: this._timestampService.now(),
      t: KirjanpidonLadattavanAineistonTyyppi.KIRJANPITAJAN_TILAAMA,
      v: null,
    }

    const tyojonoUri = 'tyojono/' + asiakas.avain + '/downloadable-accounting-material-1-create/' + tyojonoAvain
    const listausUri = 'customers/' + tyojonoEntry.asiakasAvain + '/downloadable-accounting-material/' + tyojonoEntry.ladattavanAineistonAvain

    await this._firebaseLemonaid.firestoreSetData(listausUri, listausEntry)
    await this._firebase.firestoreSetData(tyojonoUri, tyojonoEntry)
  }

  async lataaNyt(ladattavaAineisto: KirjanpidonLadattavaAineisto) {
    this._ladataanService.aloitaLataaminen()

    const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasObservable)

    const pyynto: KirjanpidonLadattavanAineistonLinkkipyynto = {
      asiakasAvain: asiakas.avain,
      ladattavanAineistonAvain: ladattavaAineisto.l,
    }

    this._firebase
      .functionsCall<KirjanpidonLadattavanAineistonLinkkipyynto, KirjanpidonLadattavanAineistonLinkkivastaus>('kirjanpitoAineistoLataaZipLinkkiLyhyt', pyynto)
      .then(vastaus => {
        if (vastaus.url) {
          window.open(vastaus.url, '_blank')
        }
        this._ladataanService.lopetaLataaminen()
      })
      .catch(err => {
        this._errorHandler.handleError(err)
        this._ladataanService.lopetaLataaminen()
      })
  }

  async luoLatauslinkki(ladattavaAineisto: KirjanpidonLadattavaAineisto) {
    this._ladataanService.aloitaLataaminen()

    const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasObservable)

    const pyynto: KirjanpidonLadattavanAineistonLinkkipyynto = {
      asiakasAvain: asiakas.avain,
      ladattavanAineistonAvain: ladattavaAineisto.l,
    }

    this._firebase
      .functionsCall<KirjanpidonLadattavanAineistonLinkkipyynto, KirjanpidonLadattavanAineistonLinkkivastaus>('kirjanpitoAineistoLataaZipLinkkiPitka', pyynto)
      .then(vastaus => {
        this._ladataanService.lopetaLataaminen()
        if (vastaus.url) {
          const data: KirjanpitoAineistonLatausUrlDialogData = {
            url: vastaus.url,
          }
          this._dialog.open(KirjanpitoAineistonLatausUrlDialog, { data: data })
        }
      })
      .catch(err => {
        this._errorHandler.handleError(err)
        this._ladataanService.lopetaLataaminen()
      })
  }

  async lahetaAsiakkaalle(ladattavaAineisto: KirjanpidonLadattavaAineisto) {
    // Varmistukset
    if (ladattavaAineisto.lahetettyAsiakkaalle) {
      const lahetettyDialogData: AreYouSureDialogData = {
        rightAction: 'Jatka',
        leftAction: 'Peruuta',
        text:
          `Huomaathan, että aineisto on jo lähetetty asiakkaalle ${this._dateService.muotoile(ladattavaAineisto.lahetettyAsiakkaalle.toDate(), 'dd.MM.yyyy HH:mm:ss')}` +
          (ladattavaAineisto.kuitattuNoudetuksi ? `\n\nAineisto on myös kuitattu noudetuksi ${this._dateService.muotoile(ladattavaAineisto.kuitattuNoudetuksi.toDate(), 'dd.MM.yyyy HH:mm:ss')}` : ''),
        header: 'Aineisto on jo lähetetty',
      }

      const lahetettyIsSure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data: lahetettyDialogData }).afterClosed())

      if (!lahetettyIsSure) {
        return
      }
    }

    // Tarvittavat tiedot
    const kirjanpitaja = await firstValueFrom(this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable.pipe(filter(kirjanpitaja => !!kirjanpitaja)))
    const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasObservable.pipe(filter(asiakas => !!asiakas)))

    this._ladataanService.aloitaLataaminen()
    const pyynto: AnnaKirjanpidonAineistonLahetystiedotPyynto = {
      asiakasAvain: asiakas.avain,
    }

    let resp: AnnaKirjanpidonAineistonLahetystiedotVastaus

    try {
      resp = await this._firebase.functionsCall<AnnaKirjanpidonAineistonLahetystiedotPyynto, AnnaKirjanpidonAineistonLahetystiedotVastaus>('annaKirjanpidonAineistonLahetystiedot', pyynto)
      if (resp?.e === 'ei-vastaanottajia') {
        this._snackbar.open('Aineiston lähetys epäonnistui. Asiakkaalla ei ole yhteyshenkilöä, joka olisi tunnistautunut Lemonaidissa pankkitunnuksilla.', 'OK', { duration: 5000 })
        return
      }
    } catch (e) {
      this._errorHandler.handleError(new Error('Kirjanpidon aineiston lähetystietojen haku epäonnistui tuntematon virheen takia!'))
    } finally {
      this._ladataanService.lopetaLataaminen()
    }

    const asiakkaanKayttajat = await firstValueFrom(this._asiakasService.nykyisenAsiakkaanKayttajatObservable)

    const dialogData: KirjanpitoAineistonLahetysDialogData = {
      vastaanottajat: asiakkaanKayttajat.filter(kayttaja => resp.vastaanottajat.includes(kayttaja.avain)),
      sahkopostinTeksti: resp.sahkoposti,
    }
    const result = await firstValueFrom(
      this._dialog.open<KirjanpitoAineistonLahetysDialog, any, KirjanpitoAineistonLahetysDialogResult>(KirjanpitoAineistonLahetysDialog, { data: dialogData }).afterClosed(),
    )

    if (!result) {
      return
    }

    // Luodaan työjono, joka käynnistää aineiston latauksen prosessin asiakkaalle
    const tyojonoEntry: KirjanpidonLadattavanAineistonLahetysAsiakkaalleTyojonodata = {
      asiakasAvain: asiakas.avain,
      ladattavanAineistonAvain: ladattavaAineisto.l,
      lisatty: this._timestampService.now(),
      kirjanpitaja: kirjanpitaja.uid,
      valitutVastaanottajat: result.vastaanottajat.map(kayttaja => kayttaja.avain),
    }
    const tyojonoUri = this._kirjanpitoUriService.annaAineistonLatausLahetaAsiakkaalleTyojonoUri(asiakas.avain, ladattavaAineisto.l)

    // Resetoidaan vastaanottajat uudelleenlähetystä varten
    const listausUri = 'customers/' + tyojonoEntry.asiakasAvain + '/downloadable-accounting-material/' + tyojonoEntry.ladattavanAineistonAvain
    const update: Partial<KirjanpidonLadattavaAineisto> = {
      vastaanottajat: [],
    }
    await this._firebaseLemonaid.firestoreSetData(listausUri, update, { merge: true })

    // Huom! Update on estetty Firestore-sääntötasolla, joten olemassaolevaa työjonoentryä ei voida päivittää eli useampaa lähetystä ei voi muodostua samasta aineistosta
    await this._firebase.firestoreSetData(tyojonoUri, tyojonoEntry)

    this._snackbar.open('Aineiston lähetys käynnistettiin onnistuneesti. Lähetetty -sarakkeeseen päivittyy tieto, kun työjono on käsitelty ja aineisto on lähetetty.', 'OK')
  }

  valitseTilikaudenAikavali(tilikausi: Tilikausi) {
    asetaArvo(this.lomake.alkaa, this._dateService.localDateToDate(tilikausi.alkaa))
    asetaArvo(this.lomake.loppuu, this._dateService.localDateToDate(tilikausi.loppuu))
  }
}
